/**
 * Dropdown menu primitive (toolbar streamlining, Task B —
 * docs/plans/2026-09-03-pricing-strategies-design.md §2), built on
 * @radix-ui/react-dropdown-menu the same way Select.tsx wraps
 * @radix-ui/react-select: Root/Value-equivalents re-exported bare, every
 * styled subcomponent wrapped once here. Used for the Price Calibration
 * toolbar's Filter and Sort menus (and, in a later task, the Strategy
 * menu) — content styled with the existing surface/border/shadow tokens,
 * the checked radio state reusing the same "active pill" classes
 * (`bg-accent-primary/10 border-accent-primary/40 text-accent-fg`) the old
 * scope/verdict/sort pills used.
 */
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../cn';

export const DropdownMenu = RadixDropdownMenu.Root;
export const DropdownMenuRadioGroup = RadixDropdownMenu.RadioGroup;

export const DropdownMenuTrigger = forwardRef<
  HTMLButtonElement,
  RadixDropdownMenu.DropdownMenuTriggerProps & { className?: string }
>(({ children, className, ...props }, ref) => (
  <RadixDropdownMenu.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-sm',
      'bg-surface text-fg-secondary border border-border-subtle',
      'hover:text-fg-primary hover:border-border-default',
      'transition-colors duration-fast ease-in-out-cubic',
      'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30',
      'data-[state=open]:text-fg-primary data-[state=open]:border-border-default',
      className
    )}
    {...props}
  >
    {children}
    <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
  </RadixDropdownMenu.Trigger>
));
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

export function DropdownMenuContent({
  children,
  className,
  align = 'start',
}: {
  children: ReactNode;
  className?: string;
  align?: RadixDropdownMenu.DropdownMenuContentProps['align'];
}) {
  return (
    <RadixDropdownMenu.Portal>
      <RadixDropdownMenu.Content
        align={align}
        sideOffset={4}
        className={cn(
          'z-50 min-w-[200px] max-h-80 overflow-auto',
          'bg-surface border border-border-subtle rounded-xl shadow-sm p-1',
          'text-xs',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          className
        )}
      >
        {children}
      </RadixDropdownMenu.Content>
    </RadixDropdownMenu.Portal>
  );
}

export function DropdownMenuLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixDropdownMenu.Label
      className={cn('px-2 py-1.5 text-xs font-medium text-fg-tertiary', className)}
    >
      {children}
    </RadixDropdownMenu.Label>
  );
}

export function DropdownMenuSeparator({ className }: { className?: string }) {
  return <RadixDropdownMenu.Separator className={cn('my-1 h-px bg-border-subtle', className)} />;
}

export const DropdownMenuRadioItem = forwardRef<
  HTMLDivElement,
  RadixDropdownMenu.DropdownMenuRadioItemProps & { className?: string }
>(({ children, className, ...props }, ref) => (
  <RadixDropdownMenu.RadioItem
    ref={ref}
    className={cn(
      'relative flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-default select-none',
      'border border-transparent',
      'text-fg-secondary data-[highlighted]:outline-none data-[highlighted]:bg-surface-elev',
      'data-[state=checked]:bg-accent-primary/10 data-[state=checked]:border-accent-primary/40 data-[state=checked]:text-accent-fg',
      className
    )}
    {...props}
  >
    <span className="size-3.5 inline-flex items-center justify-center shrink-0">
      <RadixDropdownMenu.ItemIndicator>
        <Check className="size-3 text-accent-fg" aria-hidden="true" />
      </RadixDropdownMenu.ItemIndicator>
    </span>
    <span className="flex-1 min-w-0">{children}</span>
  </RadixDropdownMenu.RadioItem>
));
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  RadixDropdownMenu.DropdownMenuItemProps & { className?: string }
>(({ children, className, ...props }, ref) => (
  <RadixDropdownMenu.Item
    ref={ref}
    className={cn(
      'flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-default select-none',
      'text-fg-primary data-[highlighted]:bg-surface-elev data-[highlighted]:outline-none',
      'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
      className
    )}
    {...props}
  >
    {children}
  </RadixDropdownMenu.Item>
));
DropdownMenuItem.displayName = 'DropdownMenuItem';
